import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Users, ExternalLink } from 'lucide-react';
import { LEGAL_LAST_UPDATED } from '../content/legalContent';
import { FOOTER_EXTERNAL } from '../config/footerLinks';

const RELATED_LINKS = [
  { to: '/privacy', label: 'Privacy Policy', icon: Shield },
  { to: '/terms', label: 'Terms of Use', icon: FileText },
  { to: '/guidelines', label: 'Community Guidelines', icon: Users },
];

const LegalPageLayout = ({ document, activePath }) => {
  const { title, subtitle, sections } = document;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="bg-gradient-to-br from-accent/10 via-transparent to-accent/5 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-accent mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Workloob
          </Link>
          <p className="text-xs font-semibold uppercase tracking-wider text-accent mb-3">
            Legal & Trust
          </p>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-heading mb-4">
            {title}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-3xl leading-relaxed">
            {subtitle}
          </p>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-500">
            Last updated: {LEGAL_LAST_UPDATED}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            <nav className="card p-5 space-y-1" aria-label="On this page">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                On this page
              </p>
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="block text-sm text-gray-600 dark:text-gray-400 hover:text-accent py-1.5 transition-colors"
                >
                  {section.title}
                </a>
              ))}
            </nav>

            <div className="mt-6 card p-5 bg-accent/5 border-accent/20">
              <p className="text-sm font-medium text-heading mb-2">Blockchain-powered marketplace</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                Workloob connects clients and talent worldwide with on-chain escrow, USDC payments, and community governance for fair outcomes.
              </p>
            </div>
          </aside>

          <article className="lg:col-span-8 space-y-10">
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-24">
                <h2 className="text-xl font-semibold text-heading mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
                  {section.title}
                </h2>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                  {section.paragraphs?.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                  {section.list && (
                    <ul className="list-disc pl-5 space-y-2 marker:text-accent">
                      {section.list.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.closingParagraphs?.map((paragraph, idx) => (
                    <p key={`close-${idx}`}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}

            <div className="card p-6 border-accent/20">
              <h3 className="text-lg font-semibold text-heading mb-4">Related documents</h3>
              <div className="flex flex-wrap gap-3">
                {RELATED_LINKS.filter((link) => link.to !== activePath).map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  );
                })}
                <a
                  href={FOOTER_EXTERNAL.contact}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent transition-colors"
                >
                  Contact support
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

export default LegalPageLayout;
